/**
 * PATH: apps/qa/src/pages/new-inspection/QuoteSourceStep.tsx
 * FILE_ID: QA-NEWINSPECTION-QUOTE-SOURCE-STEP-001
 * PURPOSE: Let the wizard start an inspection from a job's quotes.
 * WHY: Scope typed twice drifts; the quote already holds the work breakdown.
 */

import { AlertTriangle, FileSpreadsheet, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { QuoteSourceController } from './useQuoteSource'

export default function QuoteSourceStep({ source }: { source: QuoteSourceController }) {
  const { sheets, selectedSheetIds, preview } = source

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Start from a quote</h2>
        <p className="text-sm text-muted-foreground">
          Pick one or more quotes and the inspection is built from their scope — floors,
          areas and checklist items included. Leave everything unticked to build a blank inspection.
        </p>
      </div>

      {source.sheetsLoading ? (
        <p className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />Loading quotes for this job…
        </p>
      ) : source.sheetsError ? (
        <div role="alert" className="flex gap-2 rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          Could not load quotes for this job. You can still build a blank inspection.
        </div>
      ) : sheets.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-6 text-center">
          <FileSpreadsheet className="mx-auto mb-2 w-6 h-6 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">This job has no quotes yet</p>
          <p className="text-sm text-muted-foreground">Continue to build the inspection by hand.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {sheets.map((sheet) => {
            const isSelected = selectedSheetIds.includes(sheet.id)
            return (
              <li key={sheet.id}>
                <label
                  className={cn(
                    'flex items-start gap-3 rounded-xl border p-3 transition-colors cursor-pointer hover:bg-accent',
                    isSelected ? 'border-primary bg-primary/5' : 'border-border',
                  )}
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={isSelected}
                    onChange={() => source.toggleSheet(sheet.id)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">
                      {source.label(sheet)}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {sheet.quote_number || `Quote ${sheet.id}`}
                      {sheet.status ? ` · ${sheet.status}` : ''}
                    </span>
                  </span>
                </label>
              </li>
            )
          })}
        </ul>
      )}

      {selectedSheetIds.length > 1 && (
        <fieldset className="rounded-xl border border-border p-3">
          <legend className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Multiple quotes
          </legend>
          <div className="flex flex-wrap gap-4">
            {(['combine', 'separate'] as const).map((mode) => (
              <label key={mode} className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="radio"
                  name="qa-quote-create-mode"
                  checked={source.createMode === mode}
                  onChange={() => source.setCreateMode(mode)}
                />
                {mode === 'combine' ? 'One combined inspection' : 'Keep scopes separate'}
              </label>
            ))}
          </div>
        </fieldset>
      )}

      {selectedSheetIds.length > 0 && (
        <section aria-label="Quote scope preview" className="rounded-xl border border-primary/30 p-4">
          {source.previewLoading ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />Building the scope preview…
            </p>
          ) : source.previewError ? (
            <p role="alert" className="text-sm text-destructive">
              The scope preview failed to load. Untick the quotes to build a blank inspection instead.
            </p>
          ) : preview ? (
            <div className="space-y-3">
              {preview.plans.map((plan) => (
                <article key={plan.source_hash}>
                  <p className="text-sm font-medium text-foreground">
                    {plan.sources.map((src) => `${src.quote_number} R${src.revision}`).join(' + ')}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {plan.lines.length} scope line{plan.lines.length === 1 ? '' : 's'} across{' '}
                    {plan.sections.length} work location{plan.sections.length === 1 ? '' : 's'}
                  </p>
                  {plan.warnings.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs text-amber-700 dark:text-amber-300">
                      {plan.warnings.map((warning) => (
                        <li key={warning}>• {warning}</li>
                      ))}
                    </ul>
                  )}
                </article>
              ))}
            </div>
          ) : null}
        </section>
      )}
    </div>
  )
}
